import { Box, HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import React from 'react'
import { LuCheck } from 'react-icons/lu'

export const PITCH_DECK_AUDIT_ANALYZING_STAGES = [
  'Reading your deck and extracting slides',
  'Auditing Problem, Solution, and Market Size',
  'Reviewing Traction, Competition, and How It Works',
  'Evaluating Team, Funding, and Financials',
  'Checking cross-slide narrative coherence',
]

export type ProcessingChecklistProps = {
  /** Index of the active stage. Stages below it render done; equal renders a spinner. */
  activeIndex: number
  stages?: string[]
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const ProcessingChecklist: React.FC<ProcessingChecklistProps> = ({
  activeIndex,
  stages = PITCH_DECK_AUDIT_ANALYZING_STAGES,
}) => {
  // Reveal stages one-by-one as they activate; when complete (activeIndex >= length) show all.
  const visibleCount =
    activeIndex >= stages.length ? stages.length : Math.min(stages.length, Math.max(0, activeIndex) + 1)

  return (
    <VStack align={'stretch'} gap={1.5} width={'full'}>
      {stages.slice(0, visibleCount).map((stage, i) => {
        const isDone = i < activeIndex
        const isActive = i === activeIndex

        return (
          <HStack
            key={stage}
            animation={`${fadeInUp} 0.4s ease both`}
            borderRadius={'lg'}
            gap={2}
            paddingX={3.5}
            paddingY={3}
            transition={'background 240ms ease, border-color 240ms ease'}
            bgColor={
              isActive ? 'colorPalette.card/50' : isDone ? 'colorPalette.card/25' : 'colorPalette.card/10'
            }
          >
            <HStack boxSize={'22px'} flexShrink={0} justify={'center'}>
              {isDone ? (
                <Icon asChild boxSize={4} color={'teal.fg'}>
                  <LuCheck />
                </Icon>
              ) : isActive ? (
                <Spinner animationDuration={'0.8s'} borderWidth={'2px'} color={'orange.solid'} size={'sm'} />
              ) : (
                <Box bgColor={'colorPalette.border'} borderRadius={'full'} boxSize={'8px'} />
              )}
            </HStack>
            <Text
              fontSize={'sm'}
              fontWeight={isActive ? 'semibold' : 'medium'}
              opacity={isActive ? 1 : isDone ? 0.85 : 0.6}
            >
              {stage}
            </Text>
          </HStack>
        )
      })}
    </VStack>
  )
}
